import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useProfile } from "@/lib/useProfile";
import { useNutritionTargets } from "@/lib/useNutritionTargets";
import { sumNutrients } from "@/lib/nutrientTotals";
import { statusFor, CAFFEINE_SOFT_LIMIT_MG_PER_DAY } from "@/lib/nutritionEngine";
import type { DailyLog } from "@/types";
import ProgressRing from "@/components/ProgressRing";
import NutrientBar from "@/components/NutrientBar";
import WaterCounter from "@/components/WaterCounter";
import Toast from "@/components/Toast";
import { GestationalDiabetesBanner, ProviderDeferNote } from "@/components/ProviderNote";

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function Dashboard() {
  const { user } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const targets = useNutritionTargets(profile);
  const [log, setLog] = useState<DailyLog | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.logs.daily.get(todayIso()).then(setLog);
  }, []);

  if (profileLoading || !profile || !targets.calories) {
    return <div className="p-6 text-sm text-forest-500">Loading your dashboard…</div>;
  }

  const totals = sumNutrients(log?.foodEntries ?? []);
  const calories = totals.calories ?? 0;
  const protein = totals.protein ?? 0;
  const caffeine = totals.caffeine ?? 0;

  const highlighted = Object.entries(targets.micronutrients ?? {}).filter(
    ([, t]) => t.highlightOnDashboard
  );
  const rest = Object.entries(targets.micronutrients ?? {}).filter(
    ([, t]) => !t.highlightOnDashboard
  );

  return (
    <div className="mx-auto max-w-md px-4 pb-8 pt-[calc(env(safe-area-inset-top)+1.5rem)]">
      {targets.trimesterToast && (
        <Toast message={targets.trimesterToast} onDismiss={targets.dismissTrimesterToast} />
      )}

      <header className="mb-4">
        <p className="text-sm text-forest-500">Week {targets.gestationalWeek} · Trimester {targets.trimester}</p>
        <h1 className="text-2xl font-semibold text-forest-700">
          Hi{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
        </h1>
      </header>

      {profile.hasGestationalDiabetes && <div className="-mx-4"><GestationalDiabetesBanner /></div>}

      <div className="mt-6 grid grid-cols-2 gap-x-2 gap-y-6 rounded-card border border-line bg-white p-5">
        <ProgressRing
          label="Calories"
          value={calories}
          target={targets.calories}
          unit="kcal"
          status={statusFor(calories, targets.calories)}
        />
        <ProgressRing
          label="Protein"
          value={protein}
          target={targets.macros!.proteinG}
          unit="g"
          status={statusFor(protein, targets.macros!.proteinG)}
        />
        {highlighted.slice(0, 2).map(([key, t]) => (
          <ProgressRing
            key={key}
            label={t.label}
            value={totals[key] ?? 0}
            target={t.amount}
            unit={t.unit}
            status={statusFor(totals[key] ?? 0, t.amount)}
            size={110}
          />
        ))}
      </div>

      <WaterCounter
        waterMl={log?.waterMl ?? 0}
        onChange={(ml) => {
          api.logs.daily.upsert(todayIso(), { waterMl: ml }).then(setLog);
        }}
      />

      {caffeine > 0 && (
        <div className="mt-4 rounded-card border border-line bg-white px-4 py-3">
          <NutrientBar
            label="Caffeine"
            value={caffeine}
            target={CAFFEINE_SOFT_LIMIT_MG_PER_DAY}
            unit="mg"
            status={statusFor(caffeine, CAFFEINE_SOFT_LIMIT_MG_PER_DAY, 0)}
          />
        </div>
      )}

      <button
        onClick={() => setExpanded((e) => !e)}
        className="mt-4 flex h-tap w-full items-center justify-between rounded-card border border-line bg-white px-4 text-sm font-medium text-forest-700"
      >
        {expanded ? "Hide full nutrient list" : "See full nutrient list"}
        <span>{expanded ? "▲" : "▼"}</span>
      </button>

      {expanded && (
        <div className="mt-2 rounded-card border border-line bg-white px-4 py-2">
          {highlighted.slice(2).concat(rest).map(([key, t]) => (
            <NutrientBar
              key={key}
              label={t.label}
              value={totals[key] ?? 0}
              target={t.amount}
              unit={t.unit}
              status={statusFor(totals[key] ?? 0, t.amount)}
            />
          ))}
        </div>
      )}

      <ProviderDeferNote className="mt-6 text-center" />
    </div>
  );
}
